/**
 * Build canonical Schema.org JSON-LD payloads for the KosMedico site.
 * Centralised so every page injects consistent structured data.
 */

const SITE_URL = "https://kosmedico.zuerich";
const PHONE = "+41 76 468 47 73";
const STREET = "Thurgauerstrasse 117";
const POSTAL = "8152";
const CITY = "Zürich";
const COUNTRY = "CH";
const LOGO = `${SITE_URL}/kosmedico-logo.svg`;
const HERO_IMAGE = `${SITE_URL}/clinic-reception.jpg`;
const DOCTOR_IMAGE = `${SITE_URL}/dr-hirschhausen.jpg`;

/** Standalone Physician profile — referenced by the clinic schema. */
export const buildPhysicianLd = () => ({
  "@context": "https://schema.org",
  "@type": "Physician",
  "@id": `${SITE_URL}/#dr-vonhirschhausen`,
  name: "Dr. Karl Ernst Von Hirschhausen",
  jobTitle: "Diretor Médico · Estética Médica",
  image: DOCTOR_IMAGE,
  worksFor: { "@id": `${SITE_URL}/#clinic` },
  medicalSpecialty: ["Dermatology", "PlasticSurgery"],
  knowsLanguage: ["pt", "en", "de"],
  address: {
    "@type": "PostalAddress",
    streetAddress: STREET,
    postalCode: POSTAL,
    addressLocality: CITY,
    addressCountry: COUNTRY,
  },
});

/** Top-level local business / medical clinic. Use on the home page. */
export const buildMedicalClinicLd = () => ({
  "@context": "https://schema.org",
  "@type": ["MedicalClinic", "LocalBusiness"],
  "@id": `${SITE_URL}/#clinic`,
  name: "KosMedico Zürich",
  alternateName: "KosMedico Medizinische Ästhetik",
  url: SITE_URL,
  logo: LOGO,
  image: [HERO_IMAGE],
  telephone: PHONE,
  priceRange: "CHF 100 – CHF 1500",
  address: {
    "@type": "PostalAddress",
    streetAddress: STREET,
    postalCode: POSTAL,
    addressLocality: CITY,
    addressCountry: COUNTRY,
  },
  geo: { "@type": "GeoCoordinates", latitude: 47.4127, longitude: 8.5435 },
  areaServed: { "@type": "City", name: "Zürich" },
  medicalSpecialty: ["Dermatology", "PlasticSurgery", "CosmeticDentistry"],
  founder: { "@id": `${SITE_URL}/#dr-vonhirschhausen` },
  employee: [{ "@id": `${SITE_URL}/#dr-vonhirschhausen` }],
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.6",
    bestRating: "5",
    worstRating: "1",
    reviewCount: 6,
  },
  sameAs: [
    "https://www.google.com/maps/place/KosMedico+Medizinische+%C3%84sthetik",
  ],
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Wednesday", "Saturday"],
      opens: "09:00",
      closes: "18:00",
    },
  ],
});

/** A specific Service offering page (one per treatment). */
export const buildServiceLd = ({ name, description, slug, fromPrice }) => ({
  "@context": "https://schema.org",
  "@type": "MedicalProcedure",
  name,
  description,
  procedureType: "https://schema.org/NoninvasiveProcedure",
  bodyLocation: "Face",
  url: `${SITE_URL}/${slug}`,
  provider: { "@id": `${SITE_URL}/#clinic` },
  areaServed: { "@type": "City", name: "Zürich" },
  offers: fromPrice
    ? {
        "@type": "Offer",
        price: String(fromPrice).replace(/[^\d]/g, ""),
        priceCurrency: "CHF",
        availability: "https://schema.org/InStock",
        url: `${SITE_URL}/${slug}`,
      }
    : undefined,
});

/** FAQPage built from a list of { q, a }. */
export const buildFaqLd = (items = []) => {
  const list = Array.isArray(items) ? items : [];
  if (!list.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: list.map(({ q, a }) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  };
};

/** Breadcrumb helper. */
export const buildBreadcrumbLd = (items = []) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, idx) => ({
    "@type": "ListItem",
    position: idx + 1,
    name: item.name,
    item: `${SITE_URL}${item.path}`,
  })),
});

export { SITE_URL };
